import { useCallback, useRef, useState } from "react";
import { toaster } from "@/components/ui/toaster";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import type { AxiosError } from "axios";

import {
  BindLocalidad,
  BindProvincia,
  getBindLocalidades,
  getBindProvincias,
} from "@/services/bind.service";

export const useDomicilioData = () => {
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingLocalidades, setLoadingLocalidades] = useState(false);
  const [provincias, setProvincias] = useState<BindProvincia[]>([]);
  const [localidades, setLocalidades] = useState<BindLocalidad[]>([]);

  const localidadesCacheRef = useRef<Map<number, BindLocalidad[]>>(new Map());
  const provinciasFetchedRef = useRef(false);

  const fetchProvincias = useCallback(async () => {
    if (provinciasFetchedRef.current) return;

    try {
      setLoadingProvincias(true);
      const prov = await getBindProvincias();
      setProvincias(prov ?? []);
      provinciasFetchedRef.current = true;
    } catch (error) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "Error al cargar las provincias.",
        ),
      });
    } finally {
      setLoadingProvincias(false);
    }
  }, []);

  const fetchLocalidades = useCallback(async (provinciaId: number) => {
    if (!Number.isFinite(provinciaId) || !provinciaId) {
      setLocalidades([]);
      return;
    }

    const cached = localidadesCacheRef.current.get(provinciaId);
    if (cached) {
      setLocalidades(cached);
      return;
    }

    try {
      setLoadingLocalidades(true);
      const loc = await getBindLocalidades(provinciaId);
      const localidadesToSet = loc ?? [];
      localidadesCacheRef.current.set(provinciaId, localidadesToSet);
      setLocalidades(localidadesToSet);
    } catch (error) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "Error al cargar las localidades.",
        ),
      });
      setLocalidades([]);
    } finally {
      setLoadingLocalidades(false);
    }
  }, []);

  const clearLocalidades = useCallback(() => {
    setLocalidades([]);
  }, []);

  const getCodigoPostal = useCallback(
    (localidadId: number): string => {
      if (!Number.isFinite(localidadId) || !localidadId) return "";
      const selected = localidades.find((l) => l?.id === localidadId);
      return selected?.externalCode ?? "";
    },
    [localidades],
  );

  return {
    provincias,
    localidades,
    loadingProvincias,
    loadingLocalidades,
    fetchProvincias,
    fetchLocalidades,
    clearLocalidades,
    getCodigoPostal,
  };
};
